import { Link } from 'react-router-dom';
import { ReactElement, useEffect, useState } from 'react';
import { useCartContext } from 'CartContext';
import { vendorsForItems } from 'utils/cartUtils';
import { useLogin } from 'hooks/userHooks';
import { Button } from 'primereact/button';
import { useAuth0 } from '@auth0/auth0-react';
import { getMarketContent } from 'utils/marketContent';

export function CheckoutConfirmation(): ReactElement {
  const { lastOrder, loggedInUserId } = useCartContext();
  const [orderNumbers, setOrderNumbers] = useState<string[]>([]);
  const { isAuthenticated } = useAuth0();
  const login = useLogin();
  const market = getMarketContent();
  const marketName = market.market;
  const isMarketNameToken = marketName === 'token';

  useEffect(() => {
    if (lastOrder) {
      const vendors = vendorsForItems(lastOrder.items);
      if (vendors.length === 1) {
        setOrderNumbers([lastOrder.orderNumber]);
      } else {
        const orderNumberAsNumber = parseInt(lastOrder.orderNumber, 10);
        setOrderNumbers(
          vendors
            .map((vendor, index) =>
              `#${orderNumberAsNumber - index} - ${vendor}`.padStart(
                lastOrder.orderNumber.length,
                '0',
              ),
            )
            .sort((a, b) => a.localeCompare(b)),
        );
      }
    }
  }, [lastOrder]);

  const isDark = localStorage.getItem('isDark') === 'true';

  if (!lastOrder) {
    return <p>No orders found.</p>;
  }

  return (
    <>
      {isMarketNameToken && (
        <div className="flex flex-column align-items-start">
          <hr
            className="w-full border-none border-bottom-1 mt-3"
            style={{
              borderColor: isDark ? 'rgb(48,69,98)' : 'rgb(222, 226, 230)',
            }}
          />
          <a
            style={{ color: ' #00cfe5' }}
            className="font-bold my-6"
            href="https://unit.crowdpoint.tech/"
            target="_blank"
            rel="noreferrer"
          >
            Return to Unit Crowdpoint
          </a>
        </div>
      )}

      <div>
        {orderNumbers.length === 1 ? (
          <>
            <p className="font-bold m-0">Order placed successfully.</p>
            <p className="m-0">
              Conformation number:{' '}
              <span className="font-bold user-select">#{orderNumbers[0]}</span>
            </p>
            <p className="mt-0 mb-2">
              Email confirmation sent from:{' '}
              <span className="user-select underline">
                store@blockchainecosystem.exchange
              </span>
            </p>
          </>
        ) : (
          <>
            <p className="font-bold m-0">Order placed successfully.</p>
            <p className="m-0 mb-2">
              Your order was assigned multiple order numbers due to separate
              shipments:
            </p>
            <ul
              className="list-none m-0 mb-2 pl-2"
              style={{ listStylePosition: 'inside' }}
            >
              {orderNumbers.map(orderNumber => (
                <li className="font-bold user-select ml-0" key={orderNumber}>
                  {orderNumber}
                </li>
              ))}
            </ul>
            <p className="m-0 mb-4">
              Email confirmation sent from:{' '}
              <span className="user-select underline">
                store@blockchainecosystem.exchange
              </span>
            </p>
          </>
        )}

        {!isAuthenticated && (
          <div className="flex flex-column align-items-start">
            <hr
              className="w-full border-none border-bottom-1 mt-3"
              style={{
                borderColor: isDark ? 'rgb(48,69,98)' : 'rgb(222, 226, 230)',
              }}
            />
            <p className="my-3">
              Access your order information anytime by creating an account or
              logging in
            </p>
            {isMarketNameToken ? (
              <Button style={{ backgroundColor: ' #00cfe5' }} onClick={login}>
                Sign in
              </Button>
            ) : (
              <Button onClick={login}>Sign in</Button>
            )}
          </div>
        )}
        {loggedInUserId && (
          <Link
            to="/orders"
            className="no-underline"
            style={{ color: isDark ? '#14A8A6' : '#A80382' }}
          >
            View Orders
          </Link>
        )}
      </div>
    </>
  );
}
